import React, { useEffect, useState } from "react";
import {
  Building2,
  ShieldCheck,
  Users,
  Mail,
  MapPin,
  Database,
  LockKeyhole,
  Globe,
  Scale,
  Share2,
  Archive,
  Trash2,
  ShieldAlert,
  Network,
  FilePenLine,
  ContactRound,
} from "lucide-react";
import Card from "../components/Card";
import CardContent from "../components/CardContent";

const Privacy = () => {
  return (
    <section
      id="privacy"
      className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8"
    >
      <div className="mx-auto max-w-3xl justify-center text-center">
        <div
          className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl 
    bg-gradient-to-br from-[#107b38] to-emerald-400 
    text-white shadow-md 
    transition-all duration-300 
    group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-lg"
        >
          <ShieldCheck size={30} />
        </div>
        <p className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
          Privacy Policy for PropEdge Mobile App
        </p>
        <p className="mt-3 text-sm font-semibold tracking-widest text-slate-500">
          This Privacy Policy is issued in compliance with the Republic Act No.
          10173 and its Implementing Rules and Regulations, as enforced by the
          National Privacy Commission. This Policy explains how Phyxsius
          Solutions, Inc. ("Company," "we," "us," or "our") processes personal
          data through the Propedge mobile application (the “App”).{" "}
        </p>
        <p className="mt-3 text-sm font-semibold tracking-widest text-slate-500">
          Effective Date: May 4, 2026
        </p>
      </div>
      <Card className="mt-10 rounded-3xl border-slate-200 bg-white shadow-sm ">
        <CardContent className="p-7 group">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-100 text-blue-700 shadow-md">
                <Globe size={20} />
              </div>

              <h4 className="font-bold text-lg">Scope and Application</h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                This Policy applies to all users of the App, including:
              </p>
              <p className="mt-3">● Unit Owners</p>
              <p>● Tenants</p>
              <p>● Authorized Residents</p>
              <p className="mt-3 text-slate-600">
                It governs all personal data collected, processed, stored, and
                shared in connection with the App.
              </p>
            </div>
          </div>
          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-700 shadow-md">
                <Users size={20} />
              </div>

              <h4 className="font-bold text-lg">
                Personal Information Controller and Processor Roles
              </h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">For purposes of applicable law:</p>
              <p className="mt-3">
                ●<span className="font-bold"> Phyxsius Solutions, Inc.</span>{" "}
                acts as a
                <span className="font-bold">
                  {" "}
                  Personal Information Processor (PIP){" "}
                </span>
                for data processed on behalf of property management offices
              </p>
              <p>
                ● The
                <span className="font-bold"> Property Management Office </span>
                acts as the
                <span className="font-bold">
                  {" "}
                  Personal Information Controller (PIC)
                </span>
                , determining the purpose and means of processing
              </p>
              <p className="mt-3 text-slate-600">
                All processing activities are governed by appropriate
                <span className="font-bold">
                  Data Sharing Agreements (DSAs)
                </span>
                and/or
                <span className="font-bold">
                  Data Processing Agreements (DPAs)
                </span>
                .
              </p>
            </div>
          </div>
          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-700 shadow-md">
                <Database size={20} />
              </div>

              <h4 className="font-bold text-lg">Personal Data Collected</h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                We collect the following categories of personal data:
              </p>
              <p className="mt-3">
                ●<span className="font-bold"> Basic Information: </span>
                Name
              </p>
              <p className="mt-3">
                ●<span className="font-bold"> Property Information: </span>
                Unit number, property address, residency status
              </p>
              <p className="mt-3">
                ●<span className="font-bold"> Verification Data: </span>
                Government-issued identification or equivalent credentials
              </p>
              <p className="mt-3">
                ●<span className="font-bold"> Usage Data: </span>
                App interactions, system logs, and device information.
              </p>

              <p className="mt-3 text-slate-600">
                We adhere to the principles of
                <span className="font-bold">
                  transparency, legitimate purpose, and proportionality.
                </span>
              </p>
            </div>
          </div>
          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 shadow-md">
                <Scale size={20} />
              </div>

              <h4 className="font-bold text-lg">
                Purpose and Legal Basis for Processing
              </h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                Your personal data is processed based on one or more of the
                following lawful criteria:
              </p>
              <p className="mt-3">
                ● to carry out contractual obligations arising from any
                contracts entered into in relation to your data
              </p>
              <p>● to notify you about changes to our service;</p>
              <p>
                ● to perform data analyses (including anonymization and
                aggregation of Personal Data);
              </p>
              <p>
                ● to comply with other legitimate interests, Such as fraud
                prevention, system security, and service improvement
              </p>
              <p>● to comply with applicable laws and regulations;</p>

              <p className="mt-3 text-slate-600">
                Processing is limited strictly to declared, specified, and
                legitimate purposes.
              </p>
            </div>
          </div>
          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-orange-100 text-orange-700 shadow-md">
                <Share2 size={20} />
              </div>

              <h4 className="font-bold text-lg">Data Sharing and Disclosure</h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                We may disclose your personal data under the following
                conditions:
              </p>
            </div>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  name: "Property Management Offices (PICs)",
                  role: "• Identity verification \n • Account approval and activation \n • Accountability \n• Property administration functions",
                  text1:
                    "Your data is shared with your respective property management office for:",
                  text2:
                    "Such entities process your data under their own Privacy Policies.",
                },
                {
                  name: "Legal and Regulatory Disclosures",
                  role: "• Law and Regulation \n • Court order or lawful government request \n • Upon your request, in relation to your data",
                  text1: "We may disclose data when required by:",
                },
                {
                  name: "Service Providers",
                  role: "We may engage third-party providers (e.g., cloud hosting), subject to strict confidentiality and data protection obligations.",
                },
              ].map((person) => (
                <Card
                  key={person.name}
                  className="rounded-3xl border-slate-300 bg-white shadow-xl transition hover:-translate-y-1 hover:shadow-xxl"
                >
                  <CardContent className="p-7 text-center group">
                    <div className="flex items-center justify-center gap-3">
                      <h3 className="font-bold text-lg ">{person.name}</h3>
                    </div>
                    <div className="my-3 border-t border-slate-200" />

                    <p className="mt-3">{person.text1}</p>
                    <p className="text-sm font-bold whitespace-pre-line mt-3">
                      {person.role}
                    </p>
                    <p className="mt-3">{person.text2}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <div className="mt-8 my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 shadow-md">
                <Archive size={20} />
              </div>

              <h4 className="font-bold text-lg">Data Retention</h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                We retain personal data in accordance with:
              </p>
              <p className="mt-3">● Declared purposes</p>
              <p>● The Legal and regulatory requirements</p>
              <p>● Contractual obligations with property management offices</p>

              <h3 className="mt-3 font-bold text-lg">Retention Rules</h3>

              <p className="mt-3">
                ●<span className="font-bold"> Verification Data: </span>
                Deleted after successful verification unless otherwise required
              </p>
              <p className="mt-3">
                ●<span className="font-bold"> Account Data: </span>
                Retained while account is active and for a reasonable period
                thereafter
              </p>
              <p className="mt-3">
                ●<span className="font-bold"> Shared Data: </span>
                Subject to retention policies of the relevant Property
                Management Office (PIC)
              </p>

              <p className="mt-3 text-slate-600">
                Secure disposal procedures are implemented for all data
                scheduled for deletion.
              </p>
            </div>
          </div>
          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-100 text-rose-700 shadow-md">
                <Trash2 size={20} />
              </div>

              <h4 className="font-bold text-lg">
                Account Deletion and Data Subject Requests
              </h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                You may request{" "}
                <span className="font-bold">account deletion</span> by
                contacting:
              </p>
              <h3 className="mt-3 font-bold text-blue-600 underline">
                admin@phyxsiussolutions.com
              </h3>

              <h3 className="mt-3 font-bold text-lg">Process: </h3>

              <p className="mt-3">● Identity verification will be conducted</p>
              <p>● Account access will be deactivated</p>
              <p>
                ● Personal data under our control will be deleted or anonymized
                within a reasonable timeframe
              </p>
              <h3 className="mt-3 font-bold text-lg ">Important Limitation</h3>
              <p className="mt-3">Deletion request are subject to:</p>
              <p className="mt-3">
                ● Legal and regulatory retention requirements
              </p>
              <p>
                ● Legitimate business purposes (e.g., dispute resolution, audit
                trails)
              </p>
              <p>
                ● Existing
                <span className="font-bold">
                  {" "}
                  Data Sharing Agreements with Property Management Offices{" "}
                </span>
              </p>

              <p className="mt-3 text-slate-600">
                Data controlled by your Property Management Office must be
                requested directly from them, as they act as the
                <span className="font-bold">
                  {" "}
                  Personal Information Controller.
                </span>
              </p>
            </div>
          </div>

          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700 shadow-md">
                <ShieldCheck size={20} />
              </div>

              <h4 className="font-bold text-lg">Data Subject Rights</h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                In accordance with the Data Privacy Act, you have the right to:
              </p>

              <p className="mt-3">● Be informed</p>
              <p>● Access your personal data</p>
              <p>● Object to processing</p>
              <p>● Rectify inaccurate or incomplete data</p>
              <p>● Erasure or blocking</p>
              <p>● Data portability (where applicable)</p>
              <p>● File a complaint with the National Privacy Commission</p>

              <p className="mt-3 text-slate-600">
                Requests may be submitted to
                <span className="font-bold text-blue-600 underline">
                  {" "}
                  admin@phyxsiussolutions.com
                </span>
              </p>
            </div>
          </div>

          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-green-100 text-green-700 shadow-md">
                <LockKeyhole size={20} />
              </div>

              <h4 className="font-bold text-lg">Security Measures</h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                We implement appropriate safeguards, including:
              </p>
              <p className="mt-3">
                ● Organizational controls (policies, training, access
                limitations)
              </p>
              <p>
                ● Physical controls (implementation of physical infrastructures
                to prevent unauthorized access to personal information)
              </p>
              <p>
                ● Technical controls (encryption, firewalls, authentication
                mechanisms)
              </p>

              <p className="mt-3 text-slate-600">
                These are designed to protect personal data against:
              </p>
              <p className="mt-3">● Unauthorized access</p>
              <p>● Accidental loss or destruction</p>
              <p>● Alteration or disclosure</p>

              <p className="mt-3 text-slate-600">
                Secure disposal procedures are implemented for all data
                scheduled for deletion.
              </p>
            </div>
          </div>

          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-100 text-red-700 shadow-md">
                <ShieldAlert size={20} />
              </div>

              <h4 className="font-bold text-lg">Data Breach Management</h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                In the event of a personal data breach:
              </p>

              <p className="mt-3">
                ● We will assess the risk and impact of the breach promptly
              </p>
              <p>
                ● Affected users and the National Privacy Commission will be
                notified when required under applicable regulations
              </p>
              <p>
                ● Corrective actions will be implemented to mitigate harm, if
                any
              </p>
            </div>
          </div>

          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 shadow-md">
                <Network size={20} />
              </div>

              <h4 className="font-bold text-lg">Cross-Border Data Transfers</h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                Where personal data is transferred outside the Philippines:
              </p>

              <p className="mt-3">
                ● We ensure adequate safeguards consistent with NPC requirements
              </p>
              <p>
                ● Transfers are covered by contractual protections and security
                measures
              </p>
            </div>
          </div>
          <div className="my-3 border-t border-slate-200" />

          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-yellow-100 text-yellow-700 shadow-md">
                <FilePenLine size={20} />
              </div>

              <h4 className="font-bold text-lg">
                Changes to this Privacy Policy
              </h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                We may update this Policy periodically. Changes will be
                communicated via:
              </p>

              <p className="mt-3">● App notifications</p>
              <p>● Website updates</p>
            </div>
          </div>
          <div className="my-3 border-t border-slate-200" />
          <div className="mt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-100 text-teal-700 shadow-md">
                <ContactRound size={20} />
              </div>

              <h4 className="font-bold text-lg">
                Data Protection Officer (DPO) and Contact Information
              </h4>
            </div>

            <div className="mt-3 pl-14">
              <p className="text-slate-600">
                For all privacy-related concerns:
              </p>

              <div className="mt-6 rounded-2xl border-slate-300 bg-slate-50 p-6">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                      <Building2 size={18} />
                    </div>

                    <div>
                      <p className="text-sm text-slate-500">Organization</p>

                      <h4 className="font-semibold text-slate-900">
                        Phyxsius Solutions, Inc.
                      </h4>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
                      <MapPin size={18} />
                    </div>

                    <div>
                      <p className="text-sm text-slate-500">Office Address</p>

                      <p className="leading-7 text-slate-700">
                        Level 10-1 One Global Place <br />
                        Bonifacio Global City, Taguig, Philippines
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-100 text-cyan-700">
                      <Mail size={18} />
                    </div>

                    <div>
                      <p className="text-sm text-slate-500">Email Address</p>

                      <a
                        href="mailto:DPO@phyxsiussolutions.com"
                        className="font-semibold text-cyan-700 underline underline-offset-4 transition hover:text-cyan-800"
                      >
                        admin@phyxsiussolutions.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default Privacy;
